import { useJsonForms, withJsonFormsControlProps } from "@jsonforms/react";
import { ControlProps } from "@jsonforms/core";
import { useEffect, useState, useRef } from "react";
import { getAllCitiesByStates } from "services";
import CustomSearchDropdown from "../../../../../../shadcn/components/custom/dropdown/CustomSearchDropdown";

const isPincodeBasedFetchingEnabled =
  process.env.NEXT_PUBLIC_ENABLE_PINCODE_BASED_FETCHING === "true";

// Title case helper - converts "mumbai" to "Mumbai"
const toTitleCase = (str: string): string => {
  if (!str) return "";
  return str
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

interface OptionProps {
  label?: string; // Display name
  value?: string; // Unique identifier
}

export const CityFieldTypeRenderer = ({
  data,
  handleChange,
  path,
  schema,
  uischema,
}: ControlProps) => {
  const ctx = useJsonForms();
  const dropDownOptions = uischema?.options?.additionalData || {};
  const [customError, setCustomError] = useState("");
  const [citiesData, setCitiesData] = useState<OptionProps[]>();
  const [renderKey, setRenderKey] = useState(0);
  const [firstRender, setFirstRender] = useState(true);
  const positionRef = useRef<HTMLInputElement>(null);

  const show = uischema?.options?.show;
  useEffect(() => {
    if (show == false) {
      handleChange(path, {});
    }
  }, [show]);

  useEffect(() => {
    const errorData = ctx?.core?.errors;
    let flag = false;
    errorData?.forEach((error: any) => {
      if (error?.instancePath === "/" + path) {
        setCustomError(error?.message);
        flag = true;
      } else if (
        error?.instancePath === "" &&
        error?.params?.missingProperty === path
      ) {
        setCustomError("Required Field");
        flag = true;
        return;
      }
    });

    if (!flag) setCustomError("");
    else {
      if (positionRef.current) {
        positionRef.current.scrollIntoView({
          behavior: "smooth",
        });
      }
    }
  }, [ctx?.core?.errors]);

  // Only fetch cities when using dropdown (flag is OFF)
  useEffect(() => {
    if (isPincodeBasedFetchingEnabled) return; // Skip fetching when using inputs

    if (firstRender) setFirstRender(false);
    else handleChange(path, {});

    const fetch = async () => {
      if (!ctx?.core?.data?.state?.value) return;

      try {
        const response: any = await getAllCitiesByStates([
          ctx?.core?.data?.state?.value,
        ]);
        if (response) {
          const filteredData: any = Object.values(
            response.reduce((acc: any, city: any) => {
              if (!acc[city.name]) {
                acc[city.name] = {
                  uuid: city.uuid,
                  label: city.name,
                  value: city.slug,
                };
              }
              return acc;
            }, {}),
          );

          setCitiesData(filteredData);
        }
      } catch (err) {
        console.log("error here", err);
      }
    };
    setRenderKey(renderKey + 1);
    fetch();
  }, [ctx?.core?.data?.state]);

  // When feature flag is ON, show readonly text input with prefilled value from pincode
  const getDisplayValue = () => {
    if (!data) return "";
    // data can be { label, value, uuid } or { name } (for raw values)
    // Keep label as-is from API/DB, only title case raw 'name' values
    if (data.name) return toTitleCase(data.name); // Raw value from pincode - apply title case
    if (data.label) return data.label; // Keep label as-is from API
    return "";
  };

  if (show === false) {
    return <></>;
  }

  // When feature flag is ON, render as readonly input (prefilled from pincode)
  if (isPincodeBasedFetchingEnabled) {
    const displayValue = getDisplayValue();
    return (
      <div ref={positionRef} key={renderKey} className="w-full relative">
        <div className="h-[50px] bg-gray-100 border border-gray-300 rounded">
          <div className="h-full flex items-center px-4">
            <span className="text-brand-textDark font-normal text-web-body-sm">
              {displayValue || schema?.title}
            </span>
          </div>
        </div>
        <div
          className={`absolute top-1 text-[10px] transition-all duration-300 left-3 ${
            displayValue ? "text-brand-borderGrey" : "text-brand-borderGrey"
          }`}
        >
          {schema?.title}
        </div>
        {customError && (
          <label
            htmlFor="name"
            className={"text-brand-textRed text-web-body-sm ml-2"}
          >
            {customError}
          </label>
        )}
      </div>
    );
  }

  // When feature flag is OFF, render as dropdown (old behavior)
  return (
    <div ref={positionRef} key={renderKey} className="w-full relative">
      <div className="h-[50px] bg-white border  ">
        <CustomSearchDropdown
          onValueChange={(value: any) => {
            handleChange(path, value);
          }}
          placeholderText=""
          defaultSelected={
            data !== ""
              ? citiesData?.find((item: any) => data?.value === item.value)
              : undefined
          }
          dataSource={() => {
            return Promise.resolve(citiesData || []);
          }}
          styles={{
            placeholderClass: `pr-1 ${
              data ? "pt-4" : "pt-6"
            } text-brand-textLightGrey overflow-hidden`,
            triggerOuterClass: "h-full",
            formfieldClass: "h-full",
          }}
        />
        <div
          className={`absolute top-1 text-[10px] transition-all duration-300  left-3 ${"text-brand-borderGrey"}`}
        >
          {schema?.title}
        </div>
      </div>
      {customError && (
        <label
          htmlFor="name"
          className={"text-brand-textRed text-web-body-sm ml-2"}
        >
          {customError}
        </label>
      )}
    </div>
  );
};

export default withJsonFormsControlProps(CityFieldTypeRenderer);
